import React, { useState } from 'react';
import * as Yup from 'yup';
import { useIntl } from 'react-intl';
import { Form, Formik } from 'formik';
import { useBffApiRequest } from '@/hooks/useBffApiRequest';
import { BffAuthResponse } from '@lucky7ventures/bff-types';
import BffApiService from '@/shared/bffApiService';
import { BffApiError, FrontendErrorCode } from '@/models/bff-api-error';
import { generateBffErrorMessageWithCode } from '@/utils/utils';
import { FormInput } from '@/components/FormInput';
import { ButtonWithLoader } from '@/components/Common/ButtonWithLoader';
import PromptMessage from '@/components/UI/PromptMessage';

interface FormValues {
  otp: string;
}

export function LoginOtpStep({
  username,
  password,
  onSuccessfulOtp,
}: {
  username: string;
  password: string;
  onSuccessfulOtp: (data: BffAuthResponse) => void;
}): JSX.Element {
  const intl = useIntl();
  const [customError, setCustomError] = useState<string | null>(null);
  const { loading, request } = useBffApiRequest<BffAuthResponse>();

  const validationSchema = Yup.object().shape({
    otp: Yup.string().required(intl.formatMessage({ id: 'inputs.otp.required' })),
  });

  const submitHandler = (values: FormValues) => {
    const errorCallback = (error: BffApiError): void => {
      if (error.code === FrontendErrorCode.TooManyRequests) {
        setCustomError(intl.formatMessage({ id: 'error.429' }));
      } else {
        setCustomError(
          generateBffErrorMessageWithCode(intl.formatMessage({ id: 'error.support' }), error.code),
        );
      }
    };

    request({
      apiMethod: BffApiService.loginOtp,
      payload: { otp: values.otp, username, password },
      successCallback: onSuccessfulOtp,
      errorCallback,
    });
  };

  return (
    <div id="e2e_loginOtp">
      <p className="mb-8 font-semibold">{intl.formatMessage({ id: 'login.otp.title' })}</p>
      {customError && <PromptMessage className="m-t[-24px]" type="error" message={customError} />}
      <Formik
        initialValues={{
          otp: '',
        }}
        validationSchema={validationSchema}
        onSubmit={values => submitHandler(values)}
      >
        {({ isValid }) => (
          <Form>
            <FormInput
              name="otp"
              type="text"
              label={intl.formatMessage({ id: 'inputs.otp' })}
              placeholder={intl.formatMessage({
                id: 'inputs.otp.placeholder',
              })}
            />
            <ButtonWithLoader
              disabled={!isValid || loading}
              loading={loading}
              message={intl.formatMessage({ id: 'login.login' })}
            />
          </Form>
        )}
      </Formik>
      <div className="grid grid-cols-1 gap-4">
        <div className="text-center">
          {intl.formatMessage({ id: 'login.notReceivingCode' })}{' '}
          <button
            type="button"
            className="cursor-pointer text-blue-medium underline transition hover:text-blue-light"
            onClick={() => {}}
          >
            {intl.formatMessage({ id: 'login.contactSupport' })}
          </button>
        </div>
      </div>
    </div>
  );
}
